import { isUtcZone, languagesBits, timezoneBits } from '../entropy/estimates';
import type { Collector } from './types';
import { safe, signal, unavailable } from './util';

/** A fixed instant, so formatted output differs only by locale and time zone settings. */
const SAMPLE_DATE = new Date(Date.UTC(2024, 0, 15, 13, 45, 30));
const SAMPLE_NUMBER = 1234567.891;

type Area = 'America' | 'Europe' | 'Asia' | 'Africa' | 'Australia' | 'Antarctica' | 'Pacific' | 'Atlantic' | 'Indian';

const REGION_AREAS: Record<string, Area[]> = {};
const assign = (areas: Area[], regions: string) => regions.split(' ').forEach((r) => (REGION_AREAS[r] = areas));
assign(['America'], 'US CA MX BR AR CL CO PE VE EC BO PY UY GT CU DO HN NI SV CR PA PR JM TT HT BS BB BZ GY SR');
assign(['Europe'], 'GB IE FR DE IT NL BE LU CH AT DK NO SE FI IS PL CZ SK HU RO BG GR HR SI RS BA ME MK AL UA BY LT LV EE MD MT LI MC AD SM VA XK');
assign(['Europe', 'Atlantic', 'Africa'], 'ES');
assign(['Europe', 'Atlantic'], 'PT');
assign(['Europe', 'Asia'], 'RU TR CY');
assign(['Asia'], 'JP CN TW HK MO KR KP IN PK BD LK NP ID MY SG TH VN PH KH LA MM IL PS SA AE QA KW BH OM JO LB SY IQ IR AF KZ UZ KG TJ TM MN AM GE AZ YE BT BN TL');
assign(['Africa'], 'ZA NG KE EG MA DZ TN LY GH ET TZ UG SN CI CM ZW ZM AO MZ RW SD MG BW NA ML NE BF TD SO CD CG GA');
assign(['Australia', 'Antarctica'], 'AU');
assign(['Pacific'], 'NZ FJ PG WS TO');
assign(['Indian'], 'MU MV SC');
REGION_AREAS.US = ['America', 'Pacific']; // Hawaii
REGION_AREAS.FR = ['Europe', 'America', 'Indian', 'Pacific']; // overseas departments

export interface TzMatch {
  region: string | null;
  area: string | null;
  result: 'consistent' | 'mismatch' | 'unknown';
}

/** Region of a language tag, inferring a likely one when absent ("fr" → "FR"). */
export function languageRegion(tag: string): string | null {
  try {
    const IntlLocale = (Intl as unknown as { Locale?: new (t: string) => { maximize(): { region?: string } } }).Locale;
    if (!IntlLocale) return /-([A-Z]{2})\b/i.exec(tag)?.[1]?.toUpperCase() ?? null;
    return new IntlLocale(tag).maximize().region ?? null;
  } catch {
    return null;
  }
}

/** Informational: does the time zone sit on the continent the primary language's region suggests? */
export function timezoneMatchesLanguage(languageTag: string, timeZone: string): TzMatch {
  const region = languageRegion(languageTag);
  const area = timeZone.includes('/') ? (timeZone.split('/')[0] ?? null) : null;
  if (!region || !area || isUtcZone(timeZone)) return { region, area, result: 'unknown' };
  const expected = REGION_AREAS[region];
  if (!expected) return { region, area, result: 'unknown' };
  return { region, area, result: (expected as string[]).includes(area) ? 'consistent' : 'mismatch' };
}

const formatOffset = (minutes: number) => {
  const sign = minutes <= 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

export const localeCollector: Collector = {
  id: 'locale',
  title: 'Language & time zone',
  description: 'Your language preferences, time zone and the regional formats your browser uses.',
  async collect() {
    const nav = typeof navigator === 'undefined' ? undefined : navigator;
    let resolved: Intl.ResolvedDateTimeFormatOptions | undefined;
    try {
      resolved = Intl.DateTimeFormat().resolvedOptions();
    } catch {
      resolved = undefined;
    }
    const timeZone = resolved?.timeZone;

    return Promise.all([
      safe('locale.language', 'Primary language', () =>
        nav?.language
          ? signal('locale.language', 'Primary language', nav.language)
          : unavailable('locale.language', 'Primary language', 'Not exposed'),
      ),

      safe('locale.languages', 'Preferred languages', () => {
        const langs = Array.from(nav?.languages ?? []);
        if (!langs.length) return unavailable('locale.languages', 'Preferred languages', 'Not exposed');
        // Order matters (it is your preference order), so it is not sorted.
        const distinct = new Set(langs.map((l) => l.split('-')[0]?.toLowerCase())).size;
        return signal('locale.languages', 'Preferred languages', langs, { bits: languagesBits(distinct) });
      }),

      safe('locale.timezone', 'Time zone', () =>
        timeZone
          ? signal('locale.timezone', 'Time zone', timeZone, {
              bits: timezoneBits(timeZone),
              note: isUtcZone(timeZone) ? 'UTC is what Tor Browser and Firefox resistFingerprinting report.' : undefined,
            })
          : unavailable('locale.timezone', 'Time zone', 'Intl time zone not available'),
      ),

      safe('locale.tzOffset', 'UTC offset', () => {
        const offset = new Date().getTimezoneOffset();
        return signal('locale.tzOffset', 'UTC offset', offset, {
          display: `${formatOffset(offset)} (getTimezoneOffset() = ${offset})`,
          note: 'Implied by the time zone and changes with daylight saving, so it is not scored separately.',
        });
      }),

      safe('locale.intlOptions', 'Intl defaults', () => {
        if (!resolved) return unavailable('locale.intlOptions', 'Intl defaults', 'Intl not available');
        const hourCycle = new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions().hourCycle ?? null;
        const value = {
          locale: resolved.locale,
          calendar: resolved.calendar,
          numberingSystem: resolved.numberingSystem,
          hourCycle,
          matchesNavigatorLanguage: !!nav?.language && resolved.locale.toLowerCase() === nav.language.toLowerCase(),
        };
        return signal('locale.intlOptions', 'Intl defaults', value, {
          display: `locale: ${value.locale} · calendar: ${value.calendar} · numbering: ${value.numberingSystem} · hour cycle: ${hourCycle ?? '?'}`,
          note: value.matchesNavigatorLanguage
            ? undefined
            : 'Intl locale differs from navigator.language (OS and browser language settings disagree).',
        });
      }),

      safe('locale.formats', 'Regional formatting sample', () => {
        const value = {
          number: new Intl.NumberFormat().format(SAMPLE_NUMBER),
          currency: new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(SAMPLE_NUMBER),
          date: new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'long', timeZone: 'UTC' }).format(SAMPLE_DATE),
          shortDate: new Intl.DateTimeFormat().format(SAMPLE_DATE),
          list:
            typeof Intl.ListFormat === 'function' ? new Intl.ListFormat(undefined, { type: 'conjunction' }).format(['a', 'b', 'c']) : null,
        };
        return signal('locale.formats', 'Regional formatting sample', value, {
          display: `${value.number} · ${value.currency}\n${value.date}\n${value.shortDate}${value.list ? ` · ${value.list}` : ''}`,
        });
      }),

      safe('locale.tzMatch', 'Time zone vs. language region', () => {
        if (!timeZone || !nav?.language) return unavailable('locale.tzMatch', 'Time zone vs. language region', 'Not enough data');
        const m = timezoneMatchesLanguage(nav.language, timeZone);
        const text =
          m.result === 'consistent'
            ? `Consistent (${m.region} language region, ${m.area} time zone)`
            : m.result === 'mismatch'
              ? `Different continents (${m.region} language region, ${m.area} time zone): travel, VPN, or a language you use abroad`
              : 'Cannot tell';
        return signal('locale.tzMatch', 'Time zone vs. language region', m, { display: text });
      }),
    ]);
  },
};
